import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  useTheme,
  alpha,
  Stack,
  Skeleton,
  Button,
  Paper,
  Grid,
  Chip,
  IconButton,
  LinearProgress,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Tooltip,
} from "@mui/material";
import {
  Speed as SpeedIcon,
  ErrorOutline as ErrorIcon,
  DateRange as CalendarIcon,
  DirectionsCar as CarIcon,
  Notifications as NotificationIcon,
  Check as CheckIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  LocationOn as LocationIcon,
  Support as SupportIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as LineChartIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import {
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  ScatterChart,
  Scatter,
} from "recharts";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useUser } from "../provider/UserProvider";
import axiosInstance from "../utlis/axiosInstance";

interface Statistics {
  all: number;
  running: number;
  idle: number;
  stopped: number;
  inactive: number;
  no_data: number;
  expired: number;
}

interface Device {
  id: number;
  disabled?: boolean;
  inventory?: boolean;
  attributes?: {
    latest_speed?: number;
    status?: string;
  };
  latest_position?: {
    updated_at?: number;
    gps_data?: {
      latitude?: number;
      longitude?: number;
    };
  };
  latest_heartbeat?: {
    updated_at?: number;
    gps_data?: {
      battery_power?: number;
    };
  };
}

interface DashboardAnalytics {
  totalVehicles: number;
  totalAlertsToday: number;
  totalSupportTickets: number;
  totalGeofences: number;
  totalInventory: number;
  resolvedTickets: number;
  activeGeofences: number;
  averageSpeed: number;
  fuelConsumption: number;
  maintenanceDue: number;
  driverEfficiency: number;
  routeOptimization: number;
}

type KPIKey =
  | "total"
  | "running"
  | "idle"
  | "stopped"
  | "inactive"
  | "nodata"
  | "expired";

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const [devices, setDevices] = useState<Device[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const { permissions, user } = useUser();
  // Check if user is admin
  const isAdmin = user?.user?.administrator;
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<DashboardAnalytics>({
    totalVehicles: 0,
    totalAlertsToday: 0,
    totalSupportTickets: 0,
    totalGeofences: 0,
    totalInventory: 0,
    resolvedTickets: 0,
    activeGeofences: 0,
    averageSpeed: 0,
    fuelConsumption: 0,
    maintenanceDue: 0,
    driverEfficiency: 0,
    routeOptimization: 0,
  });

  const navigate = useNavigate();

  // Dynamic data for charts - based on real device data
  const [speedData, setSpeedData] = useState([
    { name: "00:00", speed: 0, fuel: 0 },
    { name: "04:00", speed: 0, fuel: 0 },
    { name: "08:00", speed: 0, fuel: 0 },
    { name: "12:00", speed: 0, fuel: 0 },
    { name: "16:00", speed: 0, fuel: 0 },
    { name: "20:00", speed: 0, fuel: 0 },
  ]);

  const [alertTrendData, setAlertTrendData] = useState([
    { day: "Mon", alerts: 12, tickets: 8 },
    { day: "Tue", alerts: 15, tickets: 10 },
    { day: "Wed", alerts: 8, tickets: 6 },
    { day: "Thu", alerts: 20, tickets: 12 },
    { day: "Fri", alerts: 18, tickets: 9 },
    { day: "Sat", alerts: 10, tickets: 5 },
    { day: "Sun", alerts: 6, tickets: 3 },
  ]);

  const [supportTicketData, setSupportTicketData] = useState([
    { status: "Open", count: 0, color: "#ef4444" },
    { status: "In Progress", count: 0, color: "#f59e0b" },
    { status: "Resolved", count: 0, color: "#10b981" },
    { status: "Closed", count: 0, color: "#6b7280" },
  ]);

  const [inventoryData, setInventoryData] = useState([
    { name: "In Inventory", value: 0, color: "#10b981" },
    { name: "Not In Inventory", value: 0, color: "#ef4444" },
  ]);

  const [geofenceList, setGeofenceList] = useState([
    {
      name: "Warehouse Zone",
      status: "Active",
      vehicles: 15,
      color: "#10b981",
    },
    { name: "Office Area", status: "Active", vehicles: 8, color: "#10b981" },
    { name: "Factory Zone", status: "Inactive", vehicles: 3, color: "#ef4444" },
    { name: "Depot Zone", status: "Active", vehicles: 12, color: "#10b981" },
    { name: "Parking Lot", status: "Active", vehicles: 6, color: "#10b981" },
  ]);

  const geofenceData = [
    { name: "Warehouse A", active: 15, inactive: 3 },
    { name: "Office B", active: 12, inactive: 2 },
    { name: "Factory C", active: 8, inactive: 1 },
    { name: "Depot D", active: 20, inactive: 5 },
  ];

  const maintenanceData = [
    { vehicle: "Truck-001", dueDate: "2024-01-15", status: "Critical" },
    { vehicle: "Truck-002", dueDate: "2024-01-20", status: "Warning" },
    { vehicle: "Truck-003", dueDate: "2024-01-25", status: "Good" },
  ];

  const driverEfficiencyData = [
    { name: "John Doe", efficiency: 85, safety: 92, fuel: 78 },
    { name: "Jane Smith", efficiency: 92, safety: 88, fuel: 85 },
    { name: "Mike Johnson", efficiency: 78, safety: 85, fuel: 82 },
    { name: "Sarah Wilson", efficiency: 88, safety: 90, fuel: 80 },
  ];

  // Add support tickets state
  const [supportTickets, setSupportTickets] = useState<any[]>([]);
  const [supportTicketsLoading, setSupportTicketsLoading] = useState(false);
  const [supportTicketsError, setSupportTicketsError] = useState<string | null>(
    null
  );
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Skeleton components
  const KPISkeleton = () => (
    <Card sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="circular" width={32} height={32} />
        </Box>
        <Skeleton variant="text" width={60} height={40} sx={{ mb: 1 }} />
      </CardContent>
    </Card>
  );

  const ChartSkeleton = () => (
    <Card sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
      <CardHeader title={<Skeleton variant="text" width={150} height={24} />} />
      <CardContent>
        <Skeleton variant="rectangular" width="100%" height={200} />
      </CardContent>
    </Card>
  );

  const [statistics, setStatistics] = useState<Statistics>({
    all: 0,
    running: 0,
    idle: 0,
    stopped: 0,
    inactive: 0,
    no_data: 0,
    expired: 0,
  });

  const fetchDevices = async () => {
    setDevicesLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get("/gpstracker/devicehandler/", {
        params: {
          limit: 1,
          offset: 0,
          response_type: "new_response",
        },
      });

      if (response.data?.status && response.data?.responseData) {
        const responseData = response.data.responseData;
        setDevices(responseData.devices || []);
        const counts = responseData.counts || {};
        setStatistics({
          all: counts.total || 0,
          running: counts.running || 0,
          idle: counts.idle || 0,
          stopped: counts.stopped || 0,
          inactive: counts.inactive || 0,
          no_data: counts.no_data || 0,
          expired: counts.expired || 0,
        });
      } else {
        setDevices([]);
        setStatistics({
          all: 0,
          running: 0,
          idle: 0,
          stopped: 0,
          inactive: 0,
          no_data: 0,
          expired: 0,
        });
      }
    } catch (err: any) {
      console.error("Failed to load devices:", err);
      setDevices([]);
      setStatistics({
        all: 0,
        running: 0,
        idle: 0,
        stopped: 0,
        inactive: 0,
        no_data: 0,
        expired: 0,
      });
      setError("Failed to load dashboard data. Please try again.");
    } finally {
      setDevicesLoading(false);
    }
  };

  // Fetch support tickets
  const fetchSupportTickets = async () => {
    setSupportTicketsLoading(true);
    setSupportTicketsError(null);
    try {
      const response = await axiosInstance.get(
        "/gpstracker/supporttickethandler/"
      );
      if (response?.data?.responseData) {
        const ticketsData = response.data.responseData || [];
        setSupportTickets([...ticketsData].reverse());

        // Calculate support ticket counts by status
        const ticketCounts = {
          open: 0,
          in_progress: 0,
          resolved: 0,
          closed: 0,
        };

        ticketsData.forEach((ticket: any) => {
          const status = ticket.status?.toLowerCase() || "open";
          if (status === "open") {
            ticketCounts.open++;
          } else if (status === "in_progress") {
            ticketCounts.in_progress++;
          } else if (status === "resolved") {
            ticketCounts.resolved++;
          } else if (status === "closed") {
            ticketCounts.closed++;
          } else {
            // Default to open for unknown statuses
            ticketCounts.open++;
          }
        });

        // Update support ticket data with real counts
        setSupportTicketData([
          { status: "Open", count: ticketCounts.open, color: "#ef4444" },
          {
            status: "In Progress",
            count: ticketCounts.in_progress,
            color: "#f59e0b",
          },
          {
            status: "Resolved",
            count: ticketCounts.resolved,
            color: "#10b981",
          },
          { status: "Closed", count: ticketCounts.closed, color: "#6b7280" },
        ]);

        // Update analytics with real support ticket data
        setAnalytics((prev) => ({
          ...prev,
          totalSupportTickets: ticketsData.length,
          resolvedTickets: ticketCounts.resolved + ticketCounts.closed,
        }));

        // Update alert trend data with real ticket counts
        // For now, we'll use the total count for the current day
        // In a real implementation, you might want to group by date
        const currentDay = new Date().toLocaleDateString("en-US", {
          weekday: "short",
        });
        setAlertTrendData((prev) =>
          prev.map((item) =>
            item.day === currentDay
              ? { ...item, tickets: ticketsData.length }
              : item
          )
        );

        // Update last updated timestamp
        setLastUpdated(new Date());
      } else {
        setSupportTickets([]);
        // Reset to default values if no data
        setSupportTicketData([
          { status: "Open", count: 0, color: "#ef4444" },
          { status: "In Progress", count: 0, color: "#f59e0b" },
          { status: "Resolved", count: 0, color: "#10b981" },
          { status: "Closed", count: 0, color: "#6b7280" },
        ]);
      }
    } catch (err: any) {
      console.error("Failed to load support tickets:", err);
      setSupportTickets([]);
      setSupportTicketsError(
        "Failed to load support tickets. Please try again."
      );
      // Reset to default values on error
      setSupportTicketData([
        { status: "Open", count: 0, color: "#ef4444" },
        { status: "In Progress", count: 0, color: "#f59e0b" },
        { status: "Resolved", count: 0, color: "#10b981" },
        { status: "Closed", count: 0, color: "#6b7280" },
      ]);
    } finally {
      setSupportTicketsLoading(false);
    }
  };

  const renderCustomLabel = () => {
    return null;
  };

  useEffect(() => {
    fetchDevices();
    fetchSupportTickets();

    // Auto-refresh every 2 minutes
    const supportTicketInterval = setInterval(() => {
      fetchSupportTickets();
    }, 2 * 60 * 1000);

    // Cleanup function for the interval
    return () => {
      clearInterval(supportTicketInterval);
    };
  }, []);

  useEffect(() => {
    // Calculate speed data from real devices (exclude disabled and inventory devices)
    const speedDevices = devices.filter(
      (device: any) =>
        !device.disabled &&
        !device.inventory &&
        device?.attributes?.latest_speed &&
        device?.attributes?.latest_speed > 0
    );

    if (speedDevices.length > 0) {
      const avgSpeed =
        speedDevices.reduce(
          (sum: number, device: any) =>
            sum + (device?.attributes?.latest_speed || 0),
          0
        ) / speedDevices.length;

      const avgFuel =
        speedDevices.reduce((sum: number, device: any) => {
          const batteryLevel =
            device?.latest_heartbeat?.gps_data?.battery_power || 0;
          return sum + batteryLevel;
        }, 0) / speedDevices.length;

      setSpeedData((prev) =>
        prev.map((item) => ({
          ...item,
          speed: Math.round(avgSpeed),
          fuel: Math.round(avgFuel),
        }))
      );
    }

    // Support ticket data is now fetched from the API in fetchSupportTickets()
    // No need to calculate based on device statuses anymore

    // Calculate inventory data
    const inInventoryCount = devices.filter(
      (device: any) => device.inventory && !device.disabled
    ).length;
    const notInInventoryCount = devices.filter(
      (device: any) => !device.inventory && !device.disabled
    ).length;

    setInventoryData([
      { name: "In Inventory", value: inInventoryCount, color: "#10b981" },
      {
        name: "Not In Inventory",
        value: notInInventoryCount,
        color: "#ef4444",
      },
    ]);

    const activeDevices = devices.filter(
      (device: any) => !device.disabled && !device.inventory
    );

    setAnalytics((prev) => ({
      ...prev,
      totalVehicles: statistics.all,
      totalAlertsToday: 45,
      totalGeofences: 18,
      totalInventory: inInventoryCount,
      activeGeofences: 15,
      averageSpeed:
        activeDevices
          .filter((device: any) => device?.attributes?.latest_speed)
          .reduce(
            (sum: number, device: any) =>
              sum + (device?.attributes?.latest_speed || 0),
            0
          ) /
        Math.max(
          1,
          activeDevices.filter((device: any) => device?.attributes?.latest_speed)
            .length
        ),
      fuelConsumption:
        activeDevices.reduce(
          (sum: number, device: any) =>
            sum + (device?.latest_heartbeat?.gps_data?.battery_power || 0),
          0
        ) / Math.max(1, activeDevices.length),
      maintenanceDue: activeDevices.filter(
        (device: any) =>
          !device?.latest_position?.updated_at ||
          !device?.latest_heartbeat?.updated_at
      ).length,
      driverEfficiency: 87,
      routeOptimization: 92,
    }));
  }, [devices, statistics]);

  const kpiConfig = [
    {
      key: "total",
      title: "Vehicles",
      icon: <CarIcon sx={{ color: "rgba(255,255,255,0.7)" }} />,
      gradientColors: ["#0284F5", "#1976d2"],
      color: "#0284F5",
      value: statistics.all,
    },
    {
      key: "running",
      title: "Running",
      icon: <SpeedIcon sx={{ color: "rgba(255,255,255,0.7)" }} />,
      gradientColors: ["#43A047", "#388E3C"],
      color: "#43A047",
      value: statistics.running,
    },
    {
      key: "idle",
      title: "Idle",
      icon: <ErrorIcon sx={{ color: "rgba(255,255,255,0.7)" }} />,
      gradientColors: ["#FFA000", "#F57C00"],
      color: "#FFA000",
      value: statistics.idle,
    },
    {
      key: "stopped",
      title: "Stopped",
      icon: <TimeIcon sx={{ color: "rgba(255,255,255,0.7)" }} />,
      gradientColors: ["#D32F2F", "#C62828"],
      color: "#D32F2F",
      value: statistics.stopped,
    },
    {
      key: "inactive",
      title: "Inactive",
      icon: <NotificationIcon sx={{ color: "rgba(255,255,255,0.7)" }} />,
      gradientColors: ["#3b82f6", "#2563eb"],
      color: "#3b82f6",
      value: statistics.inactive,
    },
    {
      key: "expired",
      title: "Expired",
      icon: <CalendarIcon sx={{ color: "rgba(255,255,255,0.7)" }} />,
      gradientColors: ["#ec4899", "#db2777"],
      color: "#ec4899",
      value: statistics.expired,
    },
    // Only show inventory KPI for admin users
    ...(isAdmin
      ? [
        {
          key: "inventory",
          title: "Inventory",
          icon: <AnalyticsIcon sx={{ color: "rgba(255,255,255,0.7)" }} />,
          gradientColors: ["#8b5cf6", "#7c3aed"],
          color: "#8b5cf6",
          value: analytics.totalInventory,
        },
      ]
      : []),
    {
      key: "no_data",
      title: "No data",
      icon: <ErrorIcon sx={{ color: "rgba(255,255,255,0.7)" }} />,
      gradientColors: ["#6b7280", "#4b5563"],
      color: "#6b7280",
      value: statistics.no_data,
    },
    {
      key: "totalSupportTickets",
      title: "Tickets",
      icon: <SupportIcon sx={{ color: "rgba(255,255,255,0.7)" }} />,
      gradientColors: ["#06b6d4", "#0891b2"],
      color: "#06b6d4",
      value: analytics.totalSupportTickets,
    },
  ];

  const fleetStatusConfig = [
    { key: "running", name: "Running", color: "#43A047" },
    { key: "idle", name: "Idle", color: "#FFA000" },
    { key: "stopped", name: "Stopped", color: "#D32F2F" },
    { key: "inactive", name: "Inactive", color: "#0284F5" },
    { key: "expired", name: "Expired", color: "#e65100" },
    { key: "no_data", name: "No data", color: "#757575" },
  ];

  const vehicleStatusData = fleetStatusConfig.map((item) => ({
    name: item.name,
    value: statistics[item.key as keyof Statistics] ?? 0,
    color: item.color,
  }));

  const getStatusColor = (status: string) => {
    switch (status) {
      case "warning":
        return theme.palette.warning.main;
      case "error":
        return theme.palette.error.main;
      case "info":
        return theme.palette.info.main;
      default:
        return theme.palette.success.main;
    }
  };

  const getVehicleStatusIcon = (statusName: string) => {
    switch (statusName) {
      case "Running":
        return <SpeedIcon fontSize="small" />;
      case "Idle":
        return <ErrorIcon fontSize="small" />;
      case "Stopped":
        return <TimeIcon fontSize="small" />;
      case "Inactive":
        return <NotificationIcon fontSize="small" />;
      case "Expired":
        return <CalendarIcon fontSize="small" />;
      case "No data":
        return <ErrorIcon fontSize="small" />;
      default:
        return <CarIcon fontSize="small" />;
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        p: { xs: 1, md: 3 },
        mt: 1,
        "@keyframes pulse": {
          "0%": { opacity: 1 },
          "50%": { opacity: 0.5 },
          "100%": { opacity: 1 },
        },
      }}
    >
      {/* Dashboard Header */}

      {/* Vehicle Status Bar Chart and KPI Cards Row */}
      <Box sx={{ display: "flex", gap: 3, mb: 4 }}>
        {/* Vehicle Status Bar Chart - Left Half */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            flex: 1,
          }}
        >
          <CardHeader
            title="Vehicle Status Chart"
            titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            action={
              <IconButton>
                <BarChartIcon />
              </IconButton>
            }
          />
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={kpiConfig} barSize={30}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="title"
                  tick={(props) => {
                    const { x, y, payload } = props;
                    const kpi = kpiConfig.find(
                      (item) => item.title === payload.value
                    );
                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text
                          x={0}
                          y={0}
                          dy={16}
                          textAnchor="middle"
                          fill={kpi?.color || "#333"}
                          transform="rotate(-45)"
                          fontSize={10}
                          fontWeight="500"
                        >
                          {payload.value}
                        </text>
                      </g>
                    );
                  }}
                  height={100}
                />
                <YAxis tick={{ fontSize: 12, fill: "#333" }} />
                <ChartTooltip
                  formatter={(value: any, name: any) => [value, name]}
                  labelStyle={{ color: "#333" }}
                />
                <Bar dataKey="value" fill="#667eea" radius={[8, 8, 0, 0]}>
                  {kpiConfig.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* KPI Cards - Right Half */}
        <Box
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1.5,
            maxHeight: 400,
            overflowY: "auto",
          }}
        >
          {kpiConfig.map((kpi) => (
            <Card
              key={kpi.key}
              sx={{
                borderRadius: 2,
                background: `linear-gradient(135deg, ${kpi.gradientColors[0]} 0%, ${kpi.gradientColors[1]} 100%)`,
                color: "#fff",
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                },
              }}
              onClick={() => {
                switch (kpi.key) {
                  case "total":
                    navigate("/vehicles");
                    break;
                  case "running":
                    navigate("/vehicles/running");
                    break;
                    case "idle":
                    navigate("/vehicles/idle");
                    break;
                    case "stopped":
                    navigate("/vehicles/stopped");
                    break;
                  case "inactive":
                    navigate("/vehicles/inactive");
                    break;
                  case "totalAlertsToday":
                    navigate("/all-alerts");
                    break;
                  case "totalSupportTickets":
                    navigate("/support-tickets");
                    break;
                  case "expired":
                    navigate("/vehicles/expired");
                      break;
                  case "no_data":
                    navigate("/vehicles/no-data");
                      break;
                  case "totalGeofences":
                    navigate("/geofencing");
                    break;
                  case "inventory":
                    if (isAdmin) {
                      navigate("/inventory");
                    }
                    break;
                  default:
                    break;
                }
              }}
            >
              <CardContent sx={{ p: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.9,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "0.7rem",
                    }}
                  >
                    {kpi.title}
                  </Typography>
                  <Box sx={{ fontSize: "1rem" }}>{kpi.icon}</Box>
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  {kpi.value}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Charts Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
          gap: 3,
          mb: 4,
        }}
      >
        {/* Vehicle Status Pie Chart */}
        <Box>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              height: 400,
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
              },
              "&:focus": {
                outline: "none",
              },
              "& *": {
                outline: "none !important",
              },
            }}
          // onClick={() => navigate("/vehicles")}
          >
            <CardHeader
              title="Vehicle Status Distribution"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <IconButton>
                  <PieChartIcon />
                </IconButton>
              }
            />
            <CardContent>
              {devicesLoading ? (
                // Skeleton loader for Vehicle Status Distribution
                <Box
                  sx={{ display: "flex", alignItems: "center", height: 300 }}
                >
                  <Box
                    sx={{
                      flex: 2,
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Skeleton variant="circular" width={200} height={200} />
                  </Box>
                  <Box sx={{ flex: 1, pl: 2, minWidth: 150 }}>
                    <Skeleton
                      variant="text"
                      width="80%"
                      height={32}
                      sx={{ mb: 2 }}
                    />
                    <Stack spacing={1}>
                      {[1, 2, 3, 4, 5].map((index) => (
                        <Box
                          key={index}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Skeleton
                            variant="rectangular"
                            width={12}
                            height={12}
                            sx={{ borderRadius: 1 }}
                          />
                          <Skeleton variant="text" width="60%" height={20} />
                          <Skeleton variant="text" width="20%" height={20} />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{ display: "flex", alignItems: "center", height: 300 }}
                >
                  <Box sx={{ flex: 2, height: "100%" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={vehicleStatusData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                          labelLine={false}
                          label={renderCustomLabel}
                          onClick={(data) => {
                            if (data && data.name) {
                              const status = data.name
                                .toLowerCase()
                                .replace(" ", "-");
                              navigate(`/vehicles/${status}`);
                            }
                          }}
                          cornerRadius={8}
                          style={{ outline: "none", userSelect: "none" }}
                        >
                          {vehicleStatusData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.color}
                              style={{ cursor: "pointer" }}
                            />
                          ))}
                        </Pie>
                        <ChartTooltip />
                        <text
                          x="50%"
                          y="50%"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="24px"
                          fontWeight="bold"
                          fill="#333"
                        >
                          {vehicleStatusData.reduce(
                            (sum, item) => sum + item.value,
                            0
                          )}
                        </text>
                        <text
                          x="50%"
                          y="65%"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="12px"
                          fill="#666"
                        >
                          Total
                        </text>
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ flex: 1, pl: 2, minWidth: 150 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                      Vehicle Status Events
                    </Typography>
                    <Stack spacing={1}>
                      {vehicleStatusData.map((entry, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                            p: 1,
                            borderRadius: 1,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "rgba(0,0,0,0.04)",
                              transform: "translateX(4px)",
                            },
                          }}
                          onClick={() => {
                            const status = entry.name
                              .toLowerCase()
                              .replace(" ", "-");
                            navigate(`/vehicles/${status}`);
                          }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: 1,
                              bgcolor: entry.color,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ flex: 1, color: entry.color }}
                          >
                            {entry.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: entry.color }}
                          >
                            {entry.value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Support Tickets Status */}
        <Box>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              height: 400,
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
              },
              "&:focus": {
                outline: "none",
              },
              "& *": {
                outline: "none !important",
              },
            }}
            onClick={() => navigate("/support-tickets")}
          >
            <CardHeader
              title={
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Support Tickets Status
                  </Typography>
                  {!supportTicketsLoading && (
                    <Typography variant="caption" color="text.secondary">
                      Total:{" "}
                      {supportTicketData.reduce(
                        (sum, item) => sum + item.count,
                        0
                      )}{" "}
                      tickets
                    </Typography>
                  )}
                </Box>
              }
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchSupportTickets();
                    }}
                    disabled={supportTicketsLoading}
                    title="Refresh Support Tickets"
                  >
                    <TimelineIcon />
                  </IconButton>
                  <IconButton>
                    <SupportIcon />
                  </IconButton>
                </Box>
              }
            />
            <CardContent>
              {supportTicketsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 300,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : supportTicketsError ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 300,
                    gap: 2,
                  }}
                >
                  <ErrorIcon color="error" sx={{ fontSize: 48 }} />
                  <Typography color="error" variant="body2" textAlign="center">
                    {supportTicketsError}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchSupportTickets();
                    }}
                  >
                    Retry
                  </Button>
                </Box>
              ) : supportTicketData.every((item) => item.count === 0) ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 300,
                    gap: 2,
                  }}
                >
                  <SupportIcon color="action" sx={{ fontSize: 48 }} />
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    textAlign="center"
                  >
                    No support tickets found
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    textAlign="center"
                  >
                    Create a new ticket to get started
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{ display: "flex", alignItems: "center", height: 300 }}
                >
                  <Box sx={{ flex: 2, height: "100%" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart style={{ outline: "none" }}>
                        <Pie
                          data={supportTicketData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="count"
                          labelLine={false}
                          label={renderCustomLabel}
                          cornerRadius={8}
                          style={{ outline: "none", userSelect: "none" }}
                        >
                          {supportTicketData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <ChartTooltip />
                        <text
                          x="50%"
                          y="50%"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="24px"
                          fontWeight="bold"
                          fill="#333"
                        >
                          {supportTicketData.reduce(
                            (sum, item) => sum + item.count,
                            0
                          )}
                        </text>
                        <text
                          x="50%"
                          y="65%"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="12px"
                          fill="#666"
                        >
                          Total
                        </text>
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ flex: 1, pl: 2, minWidth: 150 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                      Support Ticket Events
                    </Typography>
                    {lastUpdated && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 2, display: "block" }}
                      >
                        Last updated: {format(lastUpdated, "dd:MM:yyyy")}
                      </Typography>
                    )}
                    <Stack spacing={1}>
                      {supportTicketData.map((entry, index) => (
                        <Box
                          key={index}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: 1,
                              bgcolor: entry.color,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              flex: 1,
                              color: entry.color,
                              fontWeight: 500,
                            }}
                          >
                            {entry.status}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ color: entry.color }}
                          >
                            {entry.count}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Device Inventory Status - Only for Admin */}
        {/* {isAdmin && (
          <Box>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                height: 400,
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
                "&:focus": {
                  outline: "none",
                },
                "& *": {
                  outline: "none !important",
                },
              }}
              onClick={() => navigate("/inventory")}
            >
              <CardHeader
                title="Device Inventory Status"
                titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
                action={
                  <IconButton>
                    <AnalyticsIcon />
                  </IconButton>
                }
              />
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", height: 300 }}
                >
                  <Box sx={{ flex: 2, height: "100%" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart style={{ outline: "none" }}>
                        <Pie
                          data={inventoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                          labelLine={false}
                          label={renderCustomLabel}
                          cornerRadius={8}
                          style={{ outline: "none", userSelect: "none" }}
                        >
                          {inventoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <ChartTooltip />
                        <text
                          x="50%"
                          y="50%"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="24px"
                          fontWeight="bold"
                          fill="#333"
                        >
                          {inventoryData.reduce(
                            (sum, item) => sum + item.value,
                            0
                          )}
                        </text>
                        <text
                          x="50%"
                          y="65%"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize="12px"
                          fill="#666"
                        >
                          Total
                        </text>
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ flex: 1, pl: 2, minWidth: 150 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                      Inventory Events
                    </Typography>
                    <Stack spacing={1}>
                      {inventoryData.map((entry, index) => (
                        <Box
                          key={index}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: 1,
                              bgcolor: entry.color,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ flex: 1, color: entry.color }}
                          >
                            {entry.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: entry.color }}
                          >
                            {entry.value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )} */}
      </Box>

      {/* Report Cards Section */}
      <Typography
        variant="h5"
        fontWeight="bold"
        sx={{ mb: 3, color: "text.primary" }}
      >
        Quick Links
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 3,
          mb: 4,
        }}
      >
        {[
          {
            title: "Geofence",
            icon: <LocationIcon />,
            color: "#f59e0b",
            path: "/geofencing",
          },
          {
            title: "Users",
            icon: <SecurityIcon />,
            color: "#667eea",
            path: "/users",
          },
          {
            title: "Group",
            icon: <AnalyticsIcon />,
            color: "#10b981",
            path: "/groups",
          },
          {
            title: "Devices",
            icon: <SpeedIcon />,
            color: "#8b5cf6",
            path: "/vehicles",
          },
          {
            title: "Profile",
            icon: <SecurityIcon />,
            color: "#ef4444",
            path: "/settings",
          },
          {
            title: "Points",
            icon: <TrendingUpIcon />,
            color: "#06b6d4",
            path: "/points-history",
          },
          {
            title: "Trip Report",
            icon: <TimelineIcon />,
            color: "#667eea",
            path: "/reports/trip",
          },
          {
            title: "Geofence Report",
            icon: <LocationIcon />,
            color: "#f59e0b",
            path: "/reports/geofencing",
          },
          {
            title: "Summary Report",
            icon: <AnalyticsIcon />,
            color: "#8b5cf6",
            path: "/reports/summary",
          },
          {
            title: "Ignition Report",
            icon: <SpeedIcon />,
            color: "#ef4444",
            path: "/reports/ignition",
          },
          {
            title: "Stop Report",
            icon: <TimeIcon />,
            color: "#6b7280",
            path: "/reports/stop",
          },
          {
            title: "Position Report",
            icon: <TimelineIcon />,
            color: "#06b6d4",
            path: "/reports/route",
          },
          // Only show inventory link for admin users
          ...(isAdmin
            ? [
              {
                title: "Inventory",
                icon: <AnalyticsIcon />,
                color: "#8b5cf6",
                path: "/inventory",
              },
            ]
            : []),
        ].map((report, index) => (
          <Card
            key={index}
            sx={{
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
              },
            }}
            onClick={() => navigate(report.path)}
          >
            <CardContent sx={{ p: 3, textAlign: "center" }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: alpha(report.color, 0.1),
                  color: report.color,
                  mx: "auto",
                  mb: 2,
                }}
              >
                {report.icon}
              </Avatar>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                {report.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Generate detailed {report.title.toLowerCase()}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Container>
  );
};

export default Dashboard;
